import React, { useState, useEffect } from 'react'

import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer'
import { IconAlertCircle, IconDownload } from '@tabler/icons-react'
import dynamic from 'next/dynamic'
import styled from 'styled-components'

import { Alert } from './BUI/Alert'
import { Button } from './BUI/Button'
import { Flexbox } from './Flexbox'

import {
  IMAGE_FORMATS,
  PDF,
  WORD_FORMATS,
  EXCEL_FORMATS,
  CSV,
} from '@/config/constants/fileTypes'
import 'react-pdf/dist/esm/Page/AnnotationLayer.css' // required otherwise massive white space after some PDFs
import {
  createDownloadLink,
  extensionFromUrl,
} from '@/modules/attachments/attachmentHelpers'
import { PDF_VIEWER_ID } from '@/modules/pdf/PdfReadyWrapper'

// Dynamic import required to ensure correct pdf.js is used
// See: https://github.com/wojtekmaj/react-pdf/issues/136#issuecomment-716643894
const PDFViewer = dynamic(
  () => import('@/components/shared-components/PDFViewer'),
  { ssr: false }
)

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 90vh;
`

const DocViewerContainer = styled.div`
  width: 100%;
  height: 90vh;
  object-fit: contain;
`

const StyledDocViewer = styled(DocViewer)`
  width: 100% !important;
  height: 100% !important;
`

const DOCUMENT_TYPE = {
  NONE: 'none',
  IMAGE: 'image',
  PDF: 'pdf',
  WORD: 'word',
  EXCEL: 'excel',
  CSV: 'csv',
}

type DocumentType = (typeof DOCUMENT_TYPE)[keyof typeof DOCUMENT_TYPE]

type Props = {
  url: string
  name: string
  contentType?: string
  containerStyle?: React.CSSProperties
  showAllPages?: boolean
}

const DocumentViewer = ({
  url,
  name,
  contentType,
  containerStyle,
  showAllPages = false,
}: Props) => {
  const [documentType, setDocumentType] = useState<DocumentType>(null)

  useEffect(() => {
    if (!url) return

    const extension = extensionFromUrl(url)

    if (IMAGE_FORMATS.includes(extension)) {
      setDocumentType(DOCUMENT_TYPE.IMAGE)
    } else if (WORD_FORMATS.includes(extension)) {
      setDocumentType(DOCUMENT_TYPE.WORD)
    } else if (EXCEL_FORMATS.includes(extension)) {
      setDocumentType(DOCUMENT_TYPE.EXCEL)
    } else if (PDF === extension) {
      setDocumentType(DOCUMENT_TYPE.PDF)
    } else if (CSV === extension) {
      setDocumentType(DOCUMENT_TYPE.CSV)
    } else {
      // When contentType is set default to image
      // (some tickets are images but their content type is wrong)
      contentType
        ? setDocumentType(DOCUMENT_TYPE.IMAGE)
        : setDocumentType(DOCUMENT_TYPE.NONE)
    }
  }, [url, contentType])

  if (!url) return null

  const docs = [
    { uri: new URL(url).toString(), url: decodeURIComponent(url).toString() },
  ]

  return (
    <>
      {documentType === null && <div className={PDF_VIEWER_ID}>Loading...</div>}
      {documentType === DOCUMENT_TYPE.NONE && (
        <Alert
          title="Unable to show file preview: Unknown file type"
          variant="danger"
          icon={<IconAlertCircle size={16} />}
        />
      )}
      {documentType === DOCUMENT_TYPE.EXCEL ||
      documentType === DOCUMENT_TYPE.CSV ? (
        <>
          <Flexbox
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap="1rem"
          >
            <Alert
              title="This file type is not supported for preview. Please download the file to view its contents."
              variant="warning"
              icon={<IconAlertCircle size={16} />}
            />
            <a href={createDownloadLink(url)}>
              <Button variant="primary-text" icon={<IconDownload size={16} />}>
                Download
              </Button>
            </a>
          </Flexbox>
        </>
      ) : (
        ''
      )}
      {documentType === DOCUMENT_TYPE.IMAGE && (
        <ImagePreview src={url} alt={name} />
      )}
      {documentType === DOCUMENT_TYPE.WORD && (
        <DocViewerContainer style={containerStyle ? containerStyle : {}}>
          <StyledDocViewer
            prefetchMethod="GET"
            documents={docs}
            initialActiveDocument={docs[1]}
            pluginRenderers={DocViewerRenderers}
          />
        </DocViewerContainer>
      )}
      {documentType === DOCUMENT_TYPE.PDF && (
        <div className={PDF_VIEWER_ID}>
          <PDFViewer showAllPages={showAllPages} url={url} />
        </div>
      )}
    </>
  )
}

export default DocumentViewer
